/**
 * @author    Nikko Renolds
 * @package		Trap Tycoon
 * @link		  https://github.com/Ni1kko/Trap-Tycoon
 * @copyright (c) Fallox Developments2026.
 * @license		MIT
 * @description Frontend component handling user authentication (login and registration).
 */

import React, { useState } from "react";
import { Skull, Lock, User, FastForward } from "lucide-react";
import { useNotify } from "./Notification";
import { callBackend } from "../utils/api";

export default function Auth({ onLoginSuccess, navigateTo }) {
  const { addNotification } = useNotify();
  const [isRegistering, setIsRegistering] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    password: "",
    ageConsent: false,
  });
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validation: Ensure age consent is checked during registration
    if (isRegistering && !formData.ageConsent) {
      setError("You must confirm you are 18+ to enter the trap.");
      return;
    }

    try {
      const endpoint = isRegistering ? "/auth/register" : "/auth/login";
      const data = await callBackend(endpoint, {
        method: "POST",
        body: formData,
      });

      if (isRegistering) {
        setIsRegistering(false);
        addNotification("Account created. Now login.", "success");
      } else {
        // Login success
        onLoginSuccess(data.token);
        addNotification("Entering the trap.", "success");
      }
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 font-mono">
      {/* Thematic Overlays */}
      <img
        src="/assets/textures/thug_grp4.png"
        alt="Thugs"
        className="absolute left-0 bottom-0 h-[30%] md:h-[35%] sm:h-[60%] object-contain opacity-20 z-0 pointer-events-none select-none grayscale"
      />
      <img
        src="/assets/textures/police_grp4.png"
        alt="Police"
        className="absolute right-0 bottom-0 h-[30%] md:h-[35%] sm:h-[60%] object-contain opacity-20 z-0 pointer-events-none select-none grayscale hidden lg:block"
      />
      <div className="w-full max-w-md border-2 border-zinc-800 bg-zinc-950 p-8 shadow-[0_0_50px_rgba(34,197,94,0.1)]">
        {/* LOGO AREA */}
        <div className="text-center mb-8">
          <div className="inline-block p-4 bg-zinc-900 border border-zinc-800 mb-4">
            <Skull size={48} className="text-green-500" />
          </div>
          <h1 className="text-4xl font-black italic text-white tracking-tighter uppercase">
            Trap <span className="text-green-500">Tycoon</span>
          </h1>
          <p className="text-[10px] text-zinc-500 uppercase tracking-[0.3em] mt-2">
            Est. 1999 | Hustle or Be Hustled
          </p>
        </div>

        {error && (
          <div className="bg-red-900/20 border border-red-900 text-red-500 p-3 text-xs mb-6 text-center uppercase font-bold">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="relative">
            <User
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
              size={18}
            />
            <input
              type="text"
              placeholder="USERNAME"
              className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white focus:outline-none focus:border-green-500 transition-colors uppercase"
              onChange={(e) =>
                setFormData({ ...formData, username: e.target.value })
              }
              required
            />
          </div>

          <div className="relative">
            <Lock
              className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600"
              size={18}
            />
            <input
              type="password"
              placeholder="PASSWORD"
              className="w-full bg-black border border-zinc-800 p-3 pl-10 text-white focus:outline-none focus:border-green-500 transition-colors uppercase"
              onChange={(e) =>
                setFormData({ ...formData, password: e.target.value })
              }
              required
            />
          </div>

          {isRegistering && (
            <div className="relative">
              <div className="flex items-center gap-3 p-2 border border-zinc-900 bg-black/50">
                <input
                  type="checkbox"
                  id="age-consent"
                  className="w-4 h-4 accent-green-600"
                  onChange={(e) =>
                    setFormData({ ...formData, ageConsent: e.target.checked })
                  }
                  required
                />
                <label
                  htmlFor="age-consent"
                  className="text-[10px] text-zinc-400 uppercase leading-tight"
                >
                  I confirm that I am{" "}
                  <span className="text-white font-bold">
                    18 years of age or older
                  </span>{" "}
                  and agree to the terms of the block.
                </label>
              </div>
            </div>
          )}

          <button className="w-full bg-green-600 hover:bg-green-400 text-black font-black py-4 transition-all uppercase italic text-xl flex items-center justify-center gap-2">
            {isRegistering ? "INITIALIZE EMPIRE" : "ENTER THE TRAP"}{" "}
            <FastForward size={20} />
          </button>
        </form>

        <div className="mt-8 text-center border-t border-zinc-900 pt-6">
          <button
            onClick={() => setIsRegistering(!isRegistering)}
            className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest underline decoration-green-900"
          >
            {isRegistering
              ? "Already a Kingpin? Login"
              : "New to the block? Register"}
          </button>
          <div className="mt-4 flex justify-center gap-4 text-xs text-zinc-500 uppercase tracking-widest">
            <button
              onClick={() => navigateTo("/terms")}
              className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest underline decoration-green-900"
            >
              Terms of Service
            </button>
            <button
              onClick={() => navigateTo("/privacy")}
              className="text-zinc-500 hover:text-white text-xs uppercase tracking-widest underline decoration-green-900"
            >
              Privacy Policy
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
